"use client";

import { useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { ArrowRight, Sparkles } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openPublicChat } from "@/lib/chat/open-public-chat";
import { cn } from "@/lib/utils";
import { LANDING_FIELD_BAR_CLASSES } from "./landing-field-bar";

const STARTERS = [
  { text: "200 pallets to Chicago by Friday", phone: true },
  { text: "Pick up 300 pallets from my dock", phone: true },
  { text: "IBC tote prices in Atlanta", phone: false },
  { text: "Truckload Dallas to Tulsa", phone: false },
] as const;

const HIDDEN_WHILE_ASKING = "[:root[data-ask-panel=open]_&]:hidden";

function useInView(ref: RefObject<HTMLElement | null>): boolean {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const heroColumn = ref.current?.parentElement;
    if (!heroColumn) return;
    const observer = new IntersectionObserver((entries) => setInView(entries.some((entry) => entry.isIntersecting)), {
      rootMargin: "-64px 0px 0px 0px",
    });
    observer.observe(heroColumn);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function HeroAskForm() {
  const [request, setRequest] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!request.trim()) {
      inputRef.current?.focus();
      return;
    }
    openPublicChat(request.trim());
    setRequest("");
  };
  return (
    <form
      onSubmit={submit}
      className={cn(LANDING_FIELD_BAR_CLASSES, "p-2.5 max-sm:flex-wrap sm:p-3")}
    >
      <Sparkles aria-hidden className="ml-2 size-5 shrink-0 text-brand-strong" />
      <Input
        ref={inputRef}
        variant="ghost"
        aria-label="What do you need?"
        maxLength={500}
        placeholder="What do you need, and where?"
        value={request}
        onChange={(event) => setRequest(event.target.value)}
        className="h-12 min-w-0 flex-1 text-base sm:text-lg"
      />
      <Button type="submit" variant="primary" className="h-12 px-5 text-[15px] max-sm:w-full">
        Get a quote
        <ArrowRight aria-hidden />
      </Button>
    </form>
  );
}

function HeroStarters() {
  return (
    <div className="flex flex-wrap gap-2">
      {STARTERS.map((starter) => (
        <button
          key={starter.text}
          type="button"
          onClick={() => openPublicChat(starter.text)}
          className={cn(
            "min-h-11 whitespace-nowrap rounded-full border border-border bg-surface px-4 text-sm text-foreground transition-colors hover:border-fg-muted",
            !starter.phone && "max-sm:hidden",
          )}
        >
          {starter.text}
        </button>
      ))}
    </div>
  );
}

export function LandingHeroAsk() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef);
  return (
    <div
      ref={rootRef}
      data-hero-ask={inView ? "in-view" : "away"}
      className={cn("flex w-full max-w-[760px] flex-col gap-4", HIDDEN_WHILE_ASKING)}
    >
      <HeroAskForm />
      <HeroStarters />
    </div>
  );
}
